import styled, { css } from 'styled-components'
import { tabletBreakpoint } from 'react-components'
import CloseIcon from '../../../../assets/icons/close.svg'
import { getBasePath } from '../../../../utils/miscellaneous-utils'
import { zIndexScale } from '../../../../utils/style-utils'

const whiteCheckUrl = `url(${getBasePath('/assets/icons/white-check.svg')})`

export const ToggleText = styled.span(
    ({ theme }) => css`
        font-family: ${theme.typography.font.family.lato};
        font-size: ${theme.typography.font.size.small};
        font-weight: ${theme.typography.font.lato.weight.semiBold};
        line-height: 1.375rem;
        letter-spacing: 0.3px;
        color: ${theme.colors.tfwBlack99};
    `,
)

export const DesktopToggleContainer = styled.div(
    ({ theme }) => css`
        display: none;
        align-items: center;
        gap: ${theme.spacing.xSmall};
        margin-left: auto;
        flex-shrink: 0;

        ${ToggleText} {
            white-space: nowrap;
        }

        @media only screen and (min-width: ${tabletBreakpoint.min}px) {
            display: flex;
        }
    `,
)

export const MobileToggleContainer = styled.label(
    ({ theme }) => css`
        display: flex;
        width: 100%;
        box-sizing: border-box;
        align-items: center;
        justify-content: space-between;
        gap: ${theme.spacing.small};
        padding: ${theme.spacing.xSmall} ${theme.spacing.medium};
        background-color: ${theme.colors.tfwGrey};
        border-radius: ${theme.typography.border.radius.medium};
        cursor: pointer;

        ${ToggleText} {
            flex: 1 1 auto;
            min-width: 0;
        }

        @media only screen and (min-width: ${tabletBreakpoint.min}px) {
            display: none;
        }
    `,
)

export const CheckboxControl = styled.label`
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    flex-shrink: 0;
`

export const ToggleCheckboxBox = styled.span(
    ({ theme }) => css`
        box-sizing: border-box;
        display: inline-block;
        width: 1.125rem;
        height: 1.125rem;
        border: 2px solid ${theme.colors.tfwBlack50};
        border-radius: ${theme.typography.border.radius.xSmall};
        background-color: ${theme.colors.white};
        background-repeat: no-repeat;
        background-position: center;
        background-size: 0.75rem;
        flex-shrink: 0;
        transition:
            background-color 0.15s ease-in-out,
            border-color 0.15s ease-in-out;
    `,
)

export const ToggleCheckboxInput = styled.input(
    ({ theme }) => css`
        display: none;

        &:checked + ${ToggleCheckboxBox} {
            background-color: ${theme.colors.tfwBlack99};
            background-image: ${whiteCheckUrl};
            border-color: ${theme.colors.tfwBlack99};
        }
    `,
)

export const NewBadge = styled.span(
    ({ theme }) => css`
        display: inline-flex;
        align-items: center;
        justify-content: center;
        height: 1.125rem;
        padding: 0 ${theme.spacing.xSmall};
        border-radius: ${theme.typography.border.radius.large};
        background-color: ${theme.colors.tfwBlack99};
        font-family: ${theme.typography.font.family.lato};
        font-size: ${theme.typography.font.size.xxSmall};
        font-weight: ${theme.typography.font.lato.weight.semiBold};
        letter-spacing: 0.4px;
        color: #eee;
        white-space: nowrap;
        flex-shrink: 0;
    `,
)

// A <span>, not a <button>: it wraps the close button below, and a <button> can't contain another
// <button> (browsers silently hoist/restructure the DOM if it does) — `tabIndex` keeps it keyboard-
// focusable so `:focus` still reveals the tooltip without a native interactive element.
export const TooltipTrigger = styled.span(
    ({ theme }) => css`
        position: relative;
        display: inline-flex;
        align-items: center;
        cursor: pointer;
        flex-shrink: 0;

        &:focus-visible {
            outline: 2px solid ${theme.colors.tfwHyperLink};
            outline-offset: 2px;
            border-radius: ${theme.typography.border.radius.medium};
        }
    `,
)

export const TooltipCloseButton = styled.button(
    ({ theme }) => css`
        position: absolute;
        top: ${theme.spacing.xSmall};
        right: ${theme.spacing.xSmall};
        display: inline-flex;
        padding: 0;
        border: none;
        background: transparent;
        cursor: pointer;
        line-height: 0;
    `,
)

export const TooltipCloseIcon = styled(CloseIcon)(
    ({ theme }) => css`
        width: 0.75rem;
        height: 0.75rem;

        path {
            stroke: ${theme.colors.white};
        }
    `,
)

export const Tooltip = styled.span<{ $forceVisible?: boolean }>(
    ({ theme, $forceVisible }) => css`
        position: absolute;
        bottom: calc(100% + 0.625rem);
        left: 0;
        z-index: ${zIndexScale.largeTags.medium};
        width: 15rem;
        max-width: 80vw;
        padding: ${theme.spacing.small};
        padding-right: ${theme.spacing.large};
        border-radius: ${theme.typography.border.radius.medium};
        background-color: ${theme.colors.tfwBlack99};
        color: ${theme.colors.white};
        font-family: ${theme.typography.font.family.lato};
        font-size: ${theme.typography.font.size.xSmall};
        font-weight: ${theme.typography.font.lato.weight.regular};
        line-height: 1.125rem;
        letter-spacing: normal;
        white-space: normal;
        text-align: left;
        opacity: 0;
        visibility: hidden;
        transition:
            opacity 0.15s ease-in-out,
            visibility 0.15s ease-in-out;

        &::after {
            content: '';
            position: absolute;
            top: 100%;
            left: 0.375rem;
            border-width: 0.375rem;
            border-style: solid;
            border-color: ${theme.colors.tfwBlack99} transparent transparent transparent;
        }

        ${TooltipTrigger}:hover &,
        ${TooltipTrigger}:focus & {
            opacity: 1;
            visibility: visible;
        }

        ${$forceVisible &&
        css`
            opacity: 1;
            visibility: visible;
        `}
    `,
)
